import { isDefined } from '@sgme/fp';
import { HStack, Select, SgDatePicker } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useUpdateUserPreferencesMutation } from '@/api/user/index.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { ALL_CALENDAR_TENORS } from '@/types/date.ts';
import { isIsoDate } from '@/utils/date.ts';
import { useDebounce } from '@/utils/hooks/debounce.ts';

const DefaultMaturityDate = () => {
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();
  const userPreferences = useAppSelector(getUserPreferences);
  const defaultCashMaturityDate = isDefined(userPreferences.defaultCashMaturityDate)
    ? userPreferences.defaultCashMaturityDate
    : undefined;

  const isSavedValueDate = isIsoDate(defaultCashMaturityDate ?? '');

  const [date, setDate] = useState(isSavedValueDate ? defaultCashMaturityDate : undefined);
  const [tenor, setTenor] = useState(!isSavedValueDate ? defaultCashMaturityDate : '--');

  const handleDateChange = useDebounce((value: string): void => {
    sgwtWebAnalytics?.trackEvent('Header', 'user preferences, cash tab, default maturity date', value);
    setDate(value);
    setTenor('--');

    const formattedDate = isDefined(value) ? format(new Date(value), 'yyyy-MM-dd') : null;
    updateUserPreferences({
      ...userPreferences,
      defaultCashMaturityDate: formattedDate,
    });
  });

  const handleTenorChange = useDebounce(({ value }: { value: string }): void => {
    sgwtWebAnalytics?.trackEvent('Header', 'user preferences, cash tab, default maturity date', value);
    setDate(undefined);
    setTenor(value);

    updateUserPreferences({
      ...userPreferences,
      defaultCashMaturityDate: value !== '--' ? value : null,
    });
  });

  return (
    <HStack horizontalAlignment="center" verticalAlignment="center" className="p-3 px-4 border-bottom">
      <label htmlFor="default-maturity-date">
        <FormattedMessage id="userPreferences.label.maturityDate" />
      </label>
      <div className="d-flex justify-content-end">
        <SgDatePicker
          className="w-50"
          onChange={handleDateChange}
          locale={userPreferences.dateInputCultureInfo}
          values={isDefined(date) ? [date] : undefined}
          selectionType="single"
          closeAfterSelect
          size="md"
          e2e="maturity"
        />

        <span className="mt-1 mx-2">
          <FormattedMessage id="Or" />
        </span>

        <Select
          data-e2e="maturity-tenor-picker"
          itemValue={(item) => item.value}
          selectedItemValue={tenor}
          itemsAsObjects={[...[{ value: '--' }], ...ALL_CALENDAR_TENORS.map((tenor) => ({ value: tenor }))]}
          itemToString={({ value }) => value}
          onChange={handleTenorChange}
          className="w-25"
        />
      </div>
    </HStack>
  );
};

export default DefaultMaturityDate;
